import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/axios";
import Sidebar from "./sidebar";
import { motion as MOTION } from "framer-motion";
import resolveImageUrl from "../../utils/imageUtils";
import {
  ClipboardList,
  CheckCircle,
  Flag,
  ClipboardCheck,
  PlusCircle,
} from "lucide-react";
import { FaChartBar } from "react-icons/fa";

// Optional but recommended
const RANKS = {
  Inspector2: 1,
  Inspector3: 2,
  Inspector4: 3,
  Inspector1: 4,
  Inspector5: 5,
  Inspector6: 6,
  Inspector7: 7,
  Inspector8: 8,
  Inspector9: 9,
};

const getEffectiveRank = (u) => {
  const username = String(u?.username || "").trim().toLowerCase();
  const flagType = String(u?.flag_verification_type || "").trim().toLowerCase();

  if (username === "inspector9") return RANKS.Inspector9;
  if (username === "inspector6") return RANKS.Inspector6;
  if (username === "inspector1") return RANKS.Inspector1;
  if (username === "inspector2") return RANKS.Inspector8;

  if (flagType === "review_cv") return RANKS.Inspector6;
  if (flagType === "review_supervisor") return RANKS.Inspector9;
  if (flagType === "add_cv") return RANKS.Inspector1;
  if (flagType === "add_supervisor") return RANKS.Inspector8;

  if (flagType === "ai") return RANKS.Inspector6;
  if (flagType === "supervisor") return RANKS.Inspector9;

  return Number(u.rank);
};

const Dashboard = () => {
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const username = String(user?.username || "").trim().toLowerCase();
  const inspectorId = user.id;
  const userRank = getEffectiveRank(user);

  const [propertiesGiven, setPropertiesGiven] = useState(0);
  const [propertiesReviewed, setPropertiesReviewed] = useState(0);
  const [propertiesPending, setPropertiesPending] = useState(0);
  const [propertiesFlagged, setPropertiesFlagged] = useState(0);
  const [propertiesResolved, setPropertiesResolved] = useState(0);
  const [propertiesNotResolved, setPropertiesNotResolved] = useState(0);
  const [_propertiesAdded, _setPropertiesAdded] = useState(0);
  const [algorithmFlaggedCount, setAlgorithmFlaggedCount] = useState(0);
  const [circleTarget, setCircleTarget] = useState(null);

  useEffect(() => {
    if (!inspectorId) return;

    // Check if this is Inspector8 (username or rank 8)
    const isInspector8User =
      username === "inspector8" || userRank === RANKS.Inspector8;

    if (isInspector8User) {
      setPropertiesGiven(0);
    } else {
      axios
        .get(`/inspector/assignments/count/${inspectorId}`)
        .then((res) => setPropertiesGiven(res.data.totalAssignments || 0));
    }

    if (userRank === RANKS.Inspector1 || isInspector8User) {
      axios
        .get("/users/my-circle-target")
        .then((res) => {
          setCircleTarget(res.data);
          if (isInspector8User) {
            setPropertiesGiven(Number(res.data.target) || 0);
          }
        })
        .catch((err) => {
          console.error("Failed to load circle target:", err);
          setCircleTarget(null);
        });
    }

    axios
      .get(`/properties/assignments/inspector-notfiltered/${inspectorId}`)
      .then((res) => {
        const list = res.data || [];
        const pending = list.filter((p) => {
          const workflow = String(p?.status || "").trim().toLowerCase();
          const isNew = !workflow || workflow === "new";
          const isPendingReview =
            workflow === "pending review" ||
            workflow === "pending_review" ||
            workflow === "pending-review" ||
            workflow === "pending";
          const isResolved = workflow === "resolved";
          const reassessRaw = String(p?.reassess ?? "").trim().toLowerCase();
          const isReviewed =
            reassessRaw === "0" ||
            reassessRaw === "1" ||
            reassessRaw === "true" ||
            reassessRaw === "false";
          return (isNew || isPendingReview) && !isResolved && !isReviewed;
        });
        setPropertiesPending(pending.length);
      })
      .catch(() => setPropertiesPending(0));

    axios
      .get(`/inspector/assigned/${inspectorId}`)
      .then((res) => setPropertiesFlagged((res.data || []).length))
      .catch(() => setPropertiesFlagged(0));

    axios
      .get(`/inspector/resolved-not-filtered/${inspectorId}`)
      .then((res) => setPropertiesResolved((res.data || []).length))
      .catch(() => setPropertiesResolved(0));

    axios
      .get(`/inspector/unresolved-not-filtered/${inspectorId}`)
      .then((res) => setPropertiesNotResolved((res.data || []).length))
      .catch(() => setPropertiesNotResolved(0));

    const reviewCountEndpoint =
      userRank === RANKS.Inspector1 || userRank === RANKS.Inspector8
        ? `/inspector/reviews/count-s1d/${inspectorId}`
        : `/inspector/reviews/count/${inspectorId}`;
    axios
      .get(reviewCountEndpoint)
      .then((res) => {
        console.log("reviews count response:", res.data);
        setPropertiesReviewed(res.data.totalReviews || 0);
      });

    axios
      .post(`/properties/added`, { userId: inspectorId })
      .then((res) => _setPropertiesAdded(res.data.properties?.length || 0));

    axios
      .get(`/inspector/algorithm-flagged-filtered/${inspectorId}`)
      .then((res) => setAlgorithmFlaggedCount((res.data || []).length))
      .catch(() => setAlgorithmFlaggedCount(0));
  }, [inspectorId, userRank, username]);

  // Calculate progress percentages
  const totalAssigned = propertiesGiven || 1; // Total properties assigned (use 1 to avoid division by zero)
  const reviewedCount = propertiesReviewed;
  const pendingCount = propertiesPending;
  const addedCount = _propertiesAdded;
  const targetCount = Number(circleTarget?.target) || 0;

  // For Inspector 1/2: Reviewed vs Pending
  const reviewedPercentage = Math.min(100, Math.round((reviewedCount / totalAssigned) * 100));
  const pendingPercentage = Math.min(100, Math.round((pendingCount / totalAssigned) * 100));

  // For Inspector8: Added by Me vs Target
  const addedPercentage =
    targetCount > 0
      ? Math.min(100, Math.round((addedCount / targetCount) * 100))
      : 0;
  const remainingPercentage = Math.max(0, 100 - addedPercentage);

  // Define all cards with unique IDs
  const cards = [
    {
      id: "propertiesReviewed",
      displayOrder: 1,
      title: "Properties Reviewed",
      count: propertiesReviewed,
      icon: <CheckCircle className="w-8 h-8 text-green-600" />,
      border: "border-green-200",
      routesByRank: {
        [RANKS.Inspector2]: "/inspector/reviewed",
        [RANKS.Inspector7]: "/inspector/reviewed",
        [RANKS.Inspector4]: "/inspector/reviewed",
        [RANKS.Inspector1]: "/inspector/reviewed-s1d",
        [RANKS.Inspector3]: "/inspector/reviewed",
        [RANKS.Inspector6]: "/inspector/reviewed",
        [RANKS.Inspector5]: "/inspector/reviewed",
        [RANKS.Inspector8]: "/inspector/reviewed-s1d",
        [RANKS.Inspector9]: "/inspector/reviewed",
      },
      hiddenRanks: [RANKS.Inspector2, RANKS.Inspector7],
      disabledRanks: [],
      showArrow: true,
      clickId: "dashboard_properties_reviewed",
    },
    {
      id: "pendingReviews",
      displayOrder: 2,
      title: "Pending Reviews",
      count: propertiesPending,
      icon: <ClipboardList className="w-8 h-8 text-blue-600" />,
      border: "border-blue-200",
      routesByRank: {
        [RANKS.Inspector3]: "/inspector/assigned",
        [RANKS.Inspector4]: "/inspector/pending-review",
        [RANKS.Inspector5]: "/inspector/pending-review",
        [RANKS.Inspector6]: "/inspector/pending-review",
        [RANKS.Inspector7]: "/inspector/pending-review",
        [RANKS.Inspector8]: "/inspector/pending-review",
        [RANKS.Inspector9]: "/inspector/pending-review",
      },
      hiddenRanks: [RANKS.Inspector1, RANKS.Inspector7, RANKS.Inspector8],
      disabledRanks: [RANKS.Inspector2, RANKS.Inspector1],
      clickId: "dashboard_pending_reviews",
    },
    {
      id: "flaggedByAlgorithm",
      displayOrder: 3,
      title: "Flagged by Algorithm",
      count: algorithmFlaggedCount,
      icon: <Flag className="w-8 h-8 text-red-600" />,
      border: "border-red-200",
      route: "/inspector/flaggedbyalgorithm",
      hiddenRanks: [
        // RANKS.Inspector1,
        RANKS.Inspector2,
        RANKS.Inspector5,
        RANKS.Inspector4,
        RANKS.Inspector3,
        RANKS.Inspector7,
        RANKS.Inspector8,
        RANKS.Inspector9,
      ],
      disabledRanks: [],
      clickId: "dashboard_flagged_by_algorithm",
    },
    {
      id: "flaggedBySupervisor",
      displayOrder: 4,
      title: "Flagged by Supervisor",
      count: propertiesFlagged,
      icon: <Flag className="w-8 h-8 text-yellow-600" />,
      border: "border-yellow-200",
      hiddenRanks: [
        RANKS.Inspector1,
        RANKS.Inspector2,
        RANKS.Inspector5,
        RANKS.Inspector7,
        // RANKS.Inspector8,
      ],
      disabledRanks: [],
      routesByRank: {
        [RANKS.Inspector1]: "/inspector/commented",
        [RANKS.Inspector8]: "/inspector/comments",
        [RANKS.Inspector3]: "/inspector/comments",
        [RANKS.Inspector4]: "/inspector/comments",
        [RANKS.Inspector6]: "/inspector/comments",
        [RANKS.Inspector9]: "/inspector/comments",
      },
      showArrow: true,
      clickId: "dashboard_flagged_by_supervisor",
    },
    {
      id: "acceptedBySupervisor",
      displayOrder: 5,
      title: "Accepted by Supervisor",
      count: propertiesResolved,
      icon: <ClipboardCheck className="w-8 h-8 text-purple-600" />,
      border: "border-purple-200",
      route: "/inspector/resolved",
      hiddenRanks: [
        RANKS.Inspector1,
        RANKS.Inspector2,
        RANKS.Inspector5,
        RANKS.Inspector7,
        RANKS.Inspector8,
      ],
      disabledRanks: [],
      clickId: "dashboard_accepted_by_supervisor",
    },
    {
      id: "resolvedProperties",
      displayOrder: 6,
      title: "Resolved",
      count: propertiesResolved,
      icon: <ClipboardCheck className="w-8 h-8 text-indigo-600" />,
      border: "border-indigo-200",
      route: "/inspector/resolved",
      hiddenRanks: [
        // RANKS.Inspector8
      ],
      disabledRanks: [],
      clickId: "dashboard_resolved_properties",
    },
    // New cards for rank 8
    {
      id: "addProperty",
      displayOrder: 1,
      title: "Add Property",
      count: null,
      icon: <PlusCircle className="w-8 h-8 text-teal-600" />,
      border: "border-teal-200",
      route: "/inspector/addProperty",
      hiddenRanks: [
        RANKS.Inspector3,
        RANKS.Inspector5,
        RANKS.Inspector6,
        RANKS.Inspector9,
      ],
      disabledRanks: [],
      showArrow: true,
      clickId: "dashboard_add_property",
    },
    {
      id: "addedByMe",
      displayOrder: 2,
      title: "Added by Me",
      count: _propertiesAdded,
      icon: <CheckCircle className="w-8 h-8 text-blue-600" />,
      border: "border-blue-200",
      route: "/inspector/addedbyme",
      hiddenRanks: [
        RANKS.Inspector3,
        RANKS.Inspector5,
        RANKS.Inspector6,
        RANKS.Inspector9,
      ],
      disabledRanks: [],
      showArrow: true,
      clickId: "dashboard_added_by_me",
    },
  ];

  // Helper function to render cards
  const renderCards = (cardList) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
      {cardList
        .filter((card) => !card.hiddenRanks.includes(userRank))
        .map((card) => {
          const isDisabled = card.disabledRanks.includes(userRank);
          return (
            <div
              key={card.id}
              onClick={() => handleCardClick(card)}
              className={`
                bg-white rounded-lg border ${card.border}
                p-3 cursor-pointer relative hover:shadow-md transition-shadow
                ${isDisabled ? "opacity-50 pointer-events-none" : ""}
              `}
              data-click-id={card.clickId}
            >
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-sm font-semibold text-gray-800 flex items-center truncate">
                  {card.displayOrder && (
                    <span className="text-xs font-bold text-gray-400 mr-1">
                      {card.displayOrder}.
                    </span>
                  )}
                  {card.title}
                </h2>
                <div className="scale-75 origin-right">
                  {card.icon}
                </div>
              </div>

              <p className="text-2xl font-bold text-gray-900">
                {card.count !== null ? card.count : '+'}
              </p>

              {card.showArrow && (
                <span className="absolute top-1/2 right-[-1.2rem] -translate-y-1/2 text-gray-300 text-lg hidden lg:block">
                  →
                </span>
              )}
            </div>
          );
        })}
    </div>
  );

  // ✅ CENTRAL CLICK HANDLER
  const handleCardClick = (card) => {
    // If card has noNavigation flag, do nothing (just return)
    if (card.noNavigation) return;

    if (card.disabledRanks.includes(userRank)) return;

    if (card.routesByRank) {
      const route = card.routesByRank[userRank];
      if (!route) {
        alert("Access denied");
        return;
      }
      navigate(route);
      return;
    }

    if (card.route) {
      navigate(card.route);
    }
  };

  // Show progress bar for Inspector1, Inspector2, and Inspector8
  const showProgressBar = userRank === RANKS.Inspector1 || userRank === RANKS.Inspector2 || userRank === RANKS.Inspector8;

  // Check if user is Inspector8 (username "inspector8" or rank 8)
  const isInspector8 =
    username === "inspector8" || userRank === RANKS.Inspector8;

  // S1D users (Flagged by CV: Inspector1, Flagged by Supervisor: Inspector8) use "Added by Me / Target" labels
  const isS1DProgressStyle = userRank === RANKS.Inspector1 || isInspector8;

  const showFlowDashboard = userRank === RANKS.Inspector6 || userRank === RANKS.Inspector9;

  const flowBoxes = {
    review: [
      {
        title: "Total Number",
        count: propertiesGiven,
        noNavigation: true,
        clickId: "flow_total_number",
      },
      {
        title: "Pending Review",
        count: pendingCount,
        routesByRank: {
          [RANKS.Inspector3]: "/inspector/assigned",
          [RANKS.Inspector4]: "/inspector/pending-review",
          [RANKS.Inspector5]: "/inspector/pending-review",
          [RANKS.Inspector6]: "/inspector/pending-review",
          [RANKS.Inspector7]: "/inspector/pending-review",
          [RANKS.Inspector8]: "/inspector/pending-review",
          [RANKS.Inspector9]: "/inspector/pending-review",
        },
        disabledRanks: [RANKS.Inspector2, RANKS.Inspector1],
        clickId: "flow_pending_review",
      },
      {
        title: "Reviewed",
        count: propertiesReviewed,
        routesByRank: {
          [RANKS.Inspector2]: "/inspector/reviewed",
          [RANKS.Inspector7]: "/inspector/reviewed",
          [RANKS.Inspector4]: "/inspector/reviewed",
          [RANKS.Inspector1]: "/inspector/reviewed-s1d",
          [RANKS.Inspector3]: "/inspector/reviewed",
          [RANKS.Inspector6]: "/inspector/reviewed",
          [RANKS.Inspector5]: "/inspector/reviewed",
          [RANKS.Inspector8]: "/inspector/reviewed-s1d",
          [RANKS.Inspector9]: "/inspector/reviewed",
        },
        clickId: "flow_reviewed",
      },
    ],
    flagging: [
      {
        title: "Flagged for Verification by Computer Vision",
        count: algorithmFlaggedCount,
        route: "/inspector/flaggedbyalgorithm",
        hiddenRanks: [RANKS.Inspector9],
        clickId: "flow_flagged_by_ai",
      },
      {
        title: "Flagged for Verification by Supervisor",
        count: propertiesFlagged,
        routesByRank: {
          [RANKS.Inspector1]: "/inspector/commented",
          [RANKS.Inspector8]: "/inspector/comments",
          [RANKS.Inspector3]: "/inspector/comments",
          [RANKS.Inspector4]: "/inspector/comments",
          [RANKS.Inspector6]: "/inspector/comments",
          [RANKS.Inspector9]: "/inspector/comments",
        },
        hiddenRanks: [RANKS.Inspector6],
        clickId: "flow_flagged_by_supervisor",
      },
    ],
    resolution: [
      {
        title: "Resolved",
        count: propertiesResolved,
        route: "/inspector/resolved",
        clickId: "flow_resolved",
        large: true,
      },
      {
        title: "Not Resolved",
        count: propertiesNotResolved,
        route: "/inspector/not-resolved",
        clickId: "flow_not_resolved",
        large: true,
      },
    ],
  };

  const renderFlowBox = (box) => {
    const hiddenRanks = box.hiddenRanks || [];
    if (hiddenRanks.includes(userRank)) return null;

    const disabledRanks = box.disabledRanks || [];
    const isDisabled = disabledRanks.includes(userRank);

    const click = () => {
      if (box.noNavigation) return;
      if (isDisabled) return;
      if (box.routesByRank) {
        const r = box.routesByRank[userRank];
        if (!r) return;
        navigate(r);
        return;
      }
      if (box.route) navigate(box.route);
    };

    return (
      <div
        key={box.title}
        onClick={click}
        className={`bg-white rounded-xl px-4 py-3 shadow-sm border border-white/30 ${box.noNavigation ? "cursor-default" : "cursor-pointer"
          } ${isDisabled ? "opacity-50 pointer-events-none" : ""}`}
        data-click-id={box.clickId}
      >
        <div className="text-xs text-gray-700">{box.title}</div>
        <div className="text-xl font-bold text-gray-900">{box.count}</div>
      </div>
    );
  };

  const renderFlowSection = (label, bgClass, labelClass, boxes) => {
    const visibleBoxes = boxes
      .map(renderFlowBox)
      .filter(Boolean);

    return (
      <div className={`rounded-2xl ${bgClass} p-3`}>
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className={`md:w-36 text-2xl font-extrabold tracking-wide ${labelClass}`}>
            {label}
          </div>
          <div className="flex-1">
            {visibleBoxes.length === 1 ? (
              <div className="flex justify-center">
                <div className="w-full max-w-sm">{visibleBoxes[0]}</div>
              </div>
            ) : (
              <div
                className={`grid gap-4 ${boxes.some((box) => box.large) ? "max-w-4xl mx-auto" : ""} ${visibleBoxes.length >= 3
                  ? "grid-cols-1 sm:grid-cols-3"
                  : "grid-cols-1 sm:grid-cols-2"
                  }`}
              >
                {visibleBoxes}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };


  const getPendingReviewRouteForRank = () => {
    if (userRank === RANKS.Inspector3) return "/inspector/assigned";
    if (
      userRank === RANKS.Inspector4 ||
      userRank === RANKS.Inspector5 ||
      userRank === RANKS.Inspector6 ||
      userRank === RANKS.Inspector7 ||
      userRank === RANKS.Inspector8 ||
      userRank === RANKS.Inspector9
    ) {
      return "/inspector/pending-review";
    }
    return null;
  };

  const formatWorkflowStatus = (raw) => {
    if (raw === null || raw === undefined) return "N/A";
    const val = String(raw).trim();
    if (!val) return "N/A";
    const key = val.toLowerCase();
    const labels = {
      new: "New",
      resolved: "Resolved",
      flagged: "Flagged",
      assigned: "Assigned",
      explained: "Explained",
    };
    return labels[key] || val;
  };

  const openPinInRelevantPage = async (pin, statusHint) => {
    const normalizedPin = String(pin || "").trim();
    if (!normalizedPin) return;

    let status = String(statusHint || "").trim().toLowerCase();
    if (!status) {
      try {
        const res = await axios.get(`/inspector/property/${encodeURIComponent(normalizedPin)}`);
        status = String(res.data?.status || "").trim().toLowerCase();
      } catch {
        status = "";
      }
    }

    if (status === "flagged") {
      navigate(`/inspector/comments?pin=${encodeURIComponent(normalizedPin)}`);
      return;
    }

    if (status === "resolved") {
      navigate(`/inspector/resolved?pin=${encodeURIComponent(normalizedPin)}`);
      return;
    }

    const pendingRoute = getPendingReviewRouteForRank();
    if (pendingRoute) {
      navigate(`${pendingRoute}?pin=${encodeURIComponent(normalizedPin)}`);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 p-3 md:p-4 overflow-hidden">
        <div className="mb-3">
          <h1 className="text-xl font-bold text-gray-900">
            Circle Dashboard
            {user?.circle && (
              <span className="text-teal-700"> — {user.circle}</span>
            )}
          </h1>
          <p className="text-sm font-semibold text-gray-600 mt-1">
            Total Properties Assigned: <span className="text-teal-700">{propertiesGiven}</span>
          </p>
        </div>

        {/* Progress Bar Section - Condensed */}
        {showProgressBar && (
          <MOTION.div
            className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm mb-3"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center">
                <FaChartBar className="text-teal-600 text-base mr-1.5" />
                <h2 className="text-sm font-semibold text-gray-700">Progress</h2>
              </div>
              {isS1DProgressStyle ? (
                <div className="flex gap-3">
                  <span className="text-[10px] text-gray-500 flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                    Added by Me: {addedCount} ({addedPercentage}%)
                  </span>
                  <span className="text-[10px] text-gray-500 flex items-center">
                    <div className="w-2 h-2 bg-gray-300 rounded-full mr-1"></div>
                    Target: {targetCount || "Not set"} ({remainingPercentage}%)
                  </span>
                </div>
              ) : (
                <div className="flex gap-3">
                  <span className="text-[10px] text-gray-500 flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                    Reviewed: {propertiesReviewed} ({reviewedPercentage}%)
                  </span>
                  <span className="text-[10px] text-gray-500 flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-1"></div>
                    Pending: {pendingCount} ({pendingPercentage}%)
                  </span>
                </div>
              )}
            </div>

            <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden flex">
              {isS1DProgressStyle ? (
                <>
                  <MOTION.div
                    initial={{ width: 0 }}
                    animate={{ width: `${addedPercentage}%` }}
                    className="h-full bg-green-500"
                  />
                  <MOTION.div
                    initial={{ width: 0 }}
                    animate={{ width: `${remainingPercentage}%` }}
                    className="h-full bg-gray-300"
                  />
                </>
              ) : (
                <>
                  <MOTION.div
                    initial={{ width: 0 }}
                    animate={{ width: `${reviewedPercentage}%` }}
                    className="h-full bg-green-500"
                  />
                  <MOTION.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pendingPercentage}%` }}
                    className="h-full bg-blue-500"
                  />
                </>
              )}
            </div>
          </MOTION.div>
        )}

        {showFlowDashboard ? (
          <div className="rounded-2xl p-3 bg-gradient-to-br from-slate-700 via-slate-500 to-slate-400">
            <div className="space-y-3">
              {renderFlowSection(
                "REVIEW",
                "bg-gradient-to-r from-slate-900 to-slate-700",
                "text-white",
                flowBoxes.review
              )}
              <div className="flex justify-center">
                <div className="w-px h-8 bg-white/60" />
              </div>
              {renderFlowSection(
                "FLAGGING",
                "bg-gradient-to-r from-blue-700 to-blue-500",
                "text-white",
                flowBoxes.flagging
              )}
              <div className="flex justify-center">
                <div className="w-px h-8 bg-white/60" />
              </div>
              {renderFlowSection(
                "RESOLUTION",
                "bg-gradient-to-r from-sky-200 to-blue-200",
                "text-slate-800",
                flowBoxes.resolution
              )}
            </div>
          </div>
        ) : userRank === RANKS.Inspector7 ? (
          // For rank 7 show Add Property and Added by Me cards
          <div>
            {renderCards([cards.find(c => c.id === "addProperty"), cards.find(c => c.id === "addedByMe")])}
          </div>
        ) : userRank === RANKS.Inspector1 ? (
          // Rank 4 (S1D - Flagged by CV): Add Property / Added by Me / Reviewed / Flagged by CV / Resolved
          <div>
            {renderCards([cards.find(c => c.id === "addProperty"), cards.find(c => c.id === "addedByMe"), cards.find(c => c.id === "propertiesReviewed")])}
            {renderCards([cards.find(c => c.id === "flaggedByAlgorithm"), cards.find(c => c.id === "resolvedProperties")])}
          </div>
        ) : userRank === RANKS.Inspector8 ? (
          // Rank 8 (S1D - Flagged by Supervisor): Add Property / Added by Me / Reviewed / Flagged by Supervisor / Resolved
          <div>
            {renderCards([cards.find(c => c.id === "addProperty"), cards.find(c => c.id === "addedByMe"), cards.find(c => c.id === "propertiesReviewed")])}
            {renderCards([cards.find(c => c.id === "flaggedBySupervisor"), cards.find(c => c.id === "resolvedProperties")])}
          </div>
        ) : (
          <>
            {/* Card Rows */}
            {renderCards([cards.find(c => c.id === "propertiesReviewed"), cards.find(c => c.id === "pendingReviews")])}
            {userRank !== RANKS.Inspector9 && renderCards([cards.find(c => c.id === "flaggedByAlgorithm")])}
            {renderCards([cards.find(c => c.id === "flaggedBySupervisor"), cards.find(c => c.id === "acceptedBySupervisor")])}
            {renderCards([cards.find(c => c.id === "resolvedProperties")])}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
